import { useCallback, useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '@backstage/plugin-permission-react';
import { ksdpChecklistApprovePermission } from '@internal/ksdp-permissions-common';
import { PendingChecklist, PendingChecklistsResponse } from '../types';

function formatDate(value: string): string {
  if (!value) return 'Unknown';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('en-GB');
}

export function PendingChecklistsPage() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const navigate = useNavigate();
  const { allowed: canApprove } = usePermission({
    permission: ksdpChecklistApprovePermission,
  });

  const [checklists, setChecklists] = useState<PendingChecklist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadChecklists = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('checklist-approval');
      const response = await fetchApi.fetch(`${baseUrl}/checklist-validation/`);

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));

        throw new Error(
          body.error ??
            `Could not load pending checklists (${response.status}).`,
        );
      }

      const body = (await response.json()) as PendingChecklistsResponse;

      setChecklists(body.checklists ?? []);
    } catch {
      setError('Could not load pending checklists.');
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    void loadChecklists();
  }, [loadChecklists]);

  if (loading) {
    return (
      <div className="primary_card">
        <p className="checklist-loading">Loading pending checklists…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checklist-error" role="alert">
        Could not load pending checklists.
      </div>
    );
  }

  return (
    <div className="card checklist-pending-card">
      <div className="checklist-section-header checklist-pending-header">
        <div className="checklist-list-heading">
          <h2>Pending audit checklist approvals</h2>

          <p>
            Review generated questions before publishing checklists for
            assessment use.
          </p>
        </div>

        <span className="checklist-count-badge">
          {checklists.length} checklist
          {checklists.length === 1 ? '' : 's'} waiting for review
        </span>
      </div>

      {checklists.length === 0 ? (
        <div className="checklist-empty-state">
          <strong>No pending checklists</strong>
          <p>
            Generated checklists waiting for expert approval will appear here.
          </p>
        </div>
      ) : (
        <div className="checklist-table-wrapper">
          <table className="checklist-table">
            <thead>
              <tr>
                <th>Standard</th>
                <th>Generated</th>
                <th>Jama project</th>
                <th aria-label="Actions" />
              </tr>
            </thead>

            <tbody>
              {checklists.map(checklist => (
                <tr key={checklist.checklist_id}>
                  <td>
                    <strong>{checklist.standard || 'Unnamed standard'}</strong>
                  </td>

                  <td>{formatDate(checklist.created_at)}</td>

                  <td>{checklist.jama_source_project}</td>

                  <td className="checklist-table-action">
                    <button
                      type="button"
                      className="action-button"
                      onClick={() =>
                        navigate(checklist.checklist_id, {
                          state: { checklist },
                        })
                      }
                    >
                      {canApprove ? 'Review' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
